import { Link } from "react-router-dom";
import SEOMeta from "@/components/base/SEOMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import HealthDisclaimer from "@/components/base/HealthDisclaimer";

export default function About() {
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Serena Raimes - Recovery Coach & Founder | Serena's Quiet Recovery Space",
    "description": "Meet Serena Raimes, recovery coach and founder of Serena's Quiet Recovery Space. A personal story of resilience, transformation, and private alcohol recovery support without labels or judgement.",
    "url": "https://serenasquietrecoveryspace.com/about",
    "about": {
      "@type": "Person",
      "name": "Serena Raimes",
      "jobTitle": "Recovery Coach",
      "description": "Sixteen years ago, Serena lost her husband and navigated life as a single parent through grief and depression. Today she is a successful entrepreneur and recovery coach dedicated to helping people change their relationship with alcohol — without labels, pressure, or judgement.",
      "url": "https://serenasquietrecoveryspace.com/about",
      "image": "https://sraimes43-pzoau.wordpress.com/wp-content/uploads/2026/06/chatgpt-image-mar-24-2026-02_49_51-pm.png",
      "worksFor": {
        "@type": "Organization",
        "name": "Serena's Quiet Recovery Space",
        "url": "https://serenasquietrecoveryspace.com"
      }
    }
  };

  return (
    <main className="min-h-screen pt-20 md:pt-24">
      <SEOMeta
        title="About Serena | Private Recovery Coach | Serena's Quiet Recovery Space"
        description="Meet Serena Raimes, founder of Quiet Recovery Space. A recovery coach with lived experience of grief, resilience, and rebuilding. Private, compassionate alcohol support without labels or judgement."
        canonicalUrl="/about"
        ogType="article"
        schema={personSchema}
      />
      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <Breadcrumbs
              items={[
                { label: "Home", href: "/" },
                { label: "About Serena" },
              ]}
            />
            <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-start mb-12 md:mb-20">
              <div className="w-full md:w-2/5">
                <div className="relative overflow-hidden rounded-lg">
                  <img
                    src="https://sraimes43-pzoau.wordpress.com/wp-content/uploads/2026/06/chatgpt-image-mar-24-2026-02_49_51-pm.png"
                    alt="Serena Raimes - Addiction Recovery Coach and Founder of Serena's Quiet Recovery Space"
                    title="Serena Raimes - Private Addiction Recovery Coach | Serena's Quiet Recovery Space"
                    width="1024"
                    height="1024"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

              <div className="w-full md:w-3/5">
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-light text-foreground-950 mb-3 leading-tight">
                  Hi, I&apos;m Serena Raimes
                </h1>
                <p className="font-body text-sm md:text-base text-primary-700 font-medium mb-6">
                  <strong>Addiction Recovery Coach</strong> &amp; Founder of <strong>Serena&apos;s Quiet Recovery Space</strong>
                </p>
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  I want to share a deeply personal story of <strong>resilience, transformation, and hope</strong>.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed">
                  Sixteen years ago, my life changed forever when my husband passed away, leaving me to navigate life as a <strong>single parent</strong> to my young son. What followed was one of the most challenging periods of my life — a journey through <strong>grief, depression, uncertainty</strong>, and the search for a way forward.
                </p>
              </div>
            </div>

            <div className="bg-background-100 rounded-lg p-6 md:p-10 mb-12 md:mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-medium text-foreground-950 mb-6">
                My Story
              </h2>
              <div className="space-y-5 max-w-3xl">
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed">
                  Over time, I realised that <strong>true healing</strong> isn&apos;t just about surviving difficult circumstances; it&apos;s about learning how to <strong>rebuild your life with purpose, meaning, and self-belief</strong>.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed">
                  Today, I am a <strong>successful entrepreneur and recovery coach</strong>, dedicated to helping others <strong>overcome addiction, break free from destructive patterns</strong>, and create lives they can be proud of.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed">
                  My mission is simple: to provide a <strong>safe, supportive space</strong> for people who are struggling silently and help them discover that <strong>lasting change is possible</strong> — one step at a time.
                </p>
              </div>
            </div>

            <div className="bg-background-50 rounded-lg p-6 md:p-10 border border-background-200/60 mb-12 md:mb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground-950 mb-6">
                My Philosophy
              </h2>
              <div className="space-y-4 max-w-3xl">
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed">
                  I believe <strong>recovery is not a one-size-fits-all journey</strong>. Some people thrive with complete sobriety; others find freedom through moderation. What matters is finding what works for you and building a life that feels genuinely fulfilling.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed">
                  I don&apos;t use labels, shame, or rigid rules. Instead, I offer a <strong>compassionate space where you can explore your relationship with alcohol honestly</strong>, set your own goals, and move at your own pace.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed">
                  True healing comes from within. My role is to <strong>guide, support, and equip you with the tools</strong> you need to discover your own strength.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
              <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-heart-pulse-line text-2xl text-accent-600" />
                </div>
                <h3 className="font-heading text-lg font-medium text-foreground-950 mb-2">
                  Compassionate
                </h3>
                <p className="font-body text-sm text-foreground-600 leading-relaxed">
                  <strong>No judgment, no labels</strong>. Just a safe space to be honest about where you are.
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-user-smile-line text-2xl text-accent-600" />
                </div>
                <h3 className="font-heading text-lg font-medium text-foreground-950 mb-2">
                  Personalised
                </h3>
                <p className="font-body text-sm text-foreground-600 leading-relaxed">
                  Your journey is unique. <strong>Every plan is tailored</strong> to your life, goals, and circumstances.
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-accent-600" />
                </div>
                <h3 className="font-heading text-lg font-medium text-foreground-950 mb-2">
                  Confidential
                </h3>
                <p className="font-body text-sm text-foreground-600 leading-relaxed">
                  Your <strong>privacy is paramount</strong>. All sessions are completely discreet and secure.
                </p>
              </div>
            </div>

            <HealthDisclaimer variant="box" />

            <div className="mt-12 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground-950 mb-4">
                Ready to take the first step?
              </h2>
              <p className="font-body text-sm text-foreground-600 mb-6 max-w-md mx-auto leading-relaxed">
                If Serena&apos;s approach resonates with you, the next step is a quiet, confidential conversation. No pressure, no obligation — just a space to explore.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to="/contact"
                  className="inline-block bg-primary-500 text-background-50 font-body text-sm font-medium px-6 py-3 rounded-md hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Get in touch
                </Link>
                <Link
                  to="/faq"
                  className="inline-block font-body text-sm text-foreground-600 hover:text-foreground-900 px-6 py-3 rounded-md border border-background-300 hover:border-background-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Read FAQs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}